/* eslint-disable no-param-reassign */
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { usePopper } from 'react-popper';
import { Key } from 'w3c-keys';
import { cloneChildren, getDOMScrollLockActions } from '../_utils';
export const PopoverUtil = (targetRef, styles, { align, contentWidth, showAlignArrow, isVisible, content, popperRef, enableScroll, onClickOutside, bottomOffset = 0, leftOffset = 0, className, isPositionFixed = false, onEscape, isReferenceVisible, autoFocusOnVisible = false, shouldEnableScrollLock = false, onKeyDown, contentContainerClassName = '', ...restProps }) => {
    const arrowRef = useRef(null);
    const popperRefElement = useRef(null);
    const domScrollLockActions = useRef(getDOMScrollLockActions());
    const contentContainerRef = useRef(null);
    const target = targetRef && targetRef.current ? targetRef.current : targetRef;
    const popper = popperRef && popperRef.current
        ? popperRef.current
        : popperRefElement.current;
    const [istargetready, setIsTargetReady] = useState(0);
    const [hasRenderedContent, setHasRenderedContent] = useState(false);
    const dropdownWidthModifier = useMemo(() => ({
        name: 'dropdownWidthModifier',
        enabled: contentWidth !== undefined,
        fn: ({ state }) => {
            state.styles.popper.minWidth = `${state.rects.reference.width}px`;
            switch (contentWidth) {
                case 'singleSelectWidth':
                    state.styles.popper.maxWidth = '240px';
                    break;
                case 'multiSelectWidth':
                    state.styles.popper.maxWidth = '320px';
                    break;
                case 'singleSelectWithSearchBarWidth':
                    state.styles.popper.width = '240px';
                    break;
                case 'multiSelectWithSearchBarWidth':
                    state.styles.popper.width = '320px';
                    break;
                case 'targetWidth':
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                    break;
                default:
            }
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
    }), [contentWidth]);
    const { styles: style, attributes, update } = usePopper(target, popper, {
        modifiers: [
            { name: 'arrow', options: { element: arrowRef.current } },
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [leftOffset, bottomOffset],
                },
            },
            dropdownWidthModifier,
        ],
        placement: align,
    });
    const updatePopperDebounce = useCallback(_.debounce((updatePopper) => {
        updatePopper();
    }, 50), []);
    useEffect(() => {
        if (isVisible) {
            window.dispatchEvent(new Event('resize'));
        }
    }, [isVisible]);
    useEffect(() => {
        var _a, _b;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onKeyDownHandler = (event) => {
            if (isVisible) {
                // Call the generic onKeyDown handler first
                if (onKeyDown) {
                    onKeyDown(event);
                }
                // Then handle escape specifically for backward compatibility
                if (onEscape && event.key === Key.Escape) {
                    onEscape(event);
                }
            }
        };
        document.addEventListener('keydown', onKeyDownHandler);
        if (autoFocusOnVisible && isVisible)
            (_b = (_a = (popperRef || popperRefElement)) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus({
                preventScroll: true,
            });
        return () => {
            document.removeEventListener('keydown', onKeyDownHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, onEscape, onKeyDown]);
    useEffect(() => {
        /**
         * Call "onClickOutside" callback when the popover is open and
         * you click anywhere outside popover and target
         */
        const handleDocumentClick = (event) => {
            if (onClickOutside && isVisible) {
                const isInsidePopper = popper && popper.contains(event.target);
                const isInsideTarget = target && target.contains(event.target);
                // Note: Must be false (not undefined or null)
                if (isInsidePopper === false && isInsideTarget === false) {
                    onClickOutside(event);
                }
            }
        };
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [popper, target, isVisible, onClickOutside]);
    useEffect(() => {
        if (update) {
            updatePopperDebounce(update);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);
    /* This is used to fix SCAL-78710 where the Action Menu inside popover goes outside the screen. With this extra rerender after target is ready,
       the refs inside Menu component are updated with the correct values. */
    useEffect(() => {
        if (target &&
            target.getBoundingClientRect &&
            !istargetready &&
            hasRenderedContent) {
            setIsTargetReady(1);
        }
    }, [target, istargetready, hasRenderedContent]);
    /* This condition is to check if the window is resized and update the alignment of arrow */
    const updatedArrowAlignment = attributes.popper && attributes.popper['data-popper-placement']
        ? attributes.popper['data-popper-placement']
        : align;
    // Determine if content actually rendered any DOM nodes (handles null/empty fragments)
    const checkHasRenderedContent = useCallback(() => {
        const container = contentContainerRef.current;
        if (!container) {
            setHasRenderedContent(false);
            return;
        }
        const hasMeaningfulNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = (node.nodeValue || '').trim();
                return text.length > 0;
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                // If the element has any child nodes that are meaningful, it's content
                for (let i = 0; i < element.childNodes.length; i += 1) {
                    if (hasMeaningfulNode(element.childNodes[i]))
                        return true;
                }
                // If it's an element with no children but has size (e.g., inline-block), consider as content
                const rect = element.getBoundingClientRect();
                return rect.width > 0 && rect.height > 0;
            }
            return false;
        };
        let result = false;
        for (let i = 0; i < container.childNodes.length; i += 1) {
            if (hasMeaningfulNode(container.childNodes[i])) {
                result = true;
                break;
            }
        }
        setHasRenderedContent(result);
    }, []);
    useEffect(() => {
        const actions = domScrollLockActions.current;
        if (actions && shouldEnableScrollLock) {
            if (hasRenderedContent && isVisible) {
                actions.disableScroll();
            }
            else {
                actions.enableScroll();
            }
        }
        return () => {
            if (domScrollLockActions.current) {
                domScrollLockActions.current.enableScroll();
            }
        };
    }, [hasRenderedContent, shouldEnableScrollLock, isVisible]);
    useEffect(() => {
        checkHasRenderedContent();
    }, [target, isVisible, content, checkHasRenderedContent]);
    const positioncss = cx(className, styles.positionable, {
        [styles[updatedArrowAlignment]]: showAlignArrow && hasRenderedContent,
    }, { [styles.enableScroll]: enableScroll }, isVisible ? styles.in : styles.out, { [styles.positionFixed]: isPositionFixed }, { [styles.referenceVisible]: isReferenceVisible });
    const ariaProps = {
        'aria-labelledby': restProps['aria-labelledby'],
        'aria-hidden': restProps['aria-hidden'],
        'aria-orientation': restProps['aria-orientation'],
    };
    const { onPointerEnter = _.noop, onPointerLeave = _.noop, } = restProps;
    const renderPopoverContent = () => (React.createElement("div", { className: positioncss, ref: popperRef || popperRefElement, style: style.popper, ...attributes.popper, tabIndex: -1, "data-testid": "popover-container", "data-ts-element": "popover", ...ariaProps, onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave },
        React.createElement("div", { ref: contentContainerRef, className: contentContainerClassName || undefined }, cloneChildren(content, { istargetready })),
        showAlignArrow && hasRenderedContent && (React.createElement("div", { ref: arrowRef, style: style.arrow, className: styles.arrow }))));
    return {
        renderPopoverContent,
    };
};
//# sourceMappingURL=popover-utils.js.map